import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import LRUCache from 'lru-cache';
import { useDebouncedEffect } from '../../hooks/useDebouncedEffect';
import { runLengthDecode } from '@clef/shared/utils';
import { createPixelatedCanvas } from '../../utils/canvas';

export const PaddingRatio = 0.25;

let textWidthCanvas = null as OffscreenCanvas | null;
const textWidthCache = {} as Record<string, number>;
const getTextWidth = (text: string) => {
  if (textWidthCache[text]) {
    return textWidthCache[text];
  }

  if (!textWidthCanvas) {
    textWidthCanvas = new OffscreenCanvas(1024, 30);
  }

  const ctx = textWidthCanvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = `bold 12px Commissioner, sans-serif`;
  const { width } = ctx.measureText(text);
  textWidthCache[text] = width;
  return width;
};

type TextPositionCalculatorProps = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  imageWidth: number;
  imageHeight: number;
  scale: number;
  text: string;
};
export const buildTextPositionCalculators = (
  props: TextPositionCalculatorProps,
  tooltipGap: number = 0,
) => {
  const { xMin, xMax, yMin, yMax, imageWidth, imageHeight, scale, text } = props;
  const boxWidth = xMax - xMin;
  const boxHeight = yMax - yMin;
  const fontSize = 12 / scale;
  const backgroundHeight = fontSize * 1.5;
  const backgroundWidth = getTextWidth(text) / scale + 2 * fontSize;

  const padding = Math.max(boxWidth, boxHeight) * PaddingRatio;

  const offset = 2 / scale;

  const top = () =>
    // there are enough space on the top
    yMin > backgroundHeight + tooltipGap && {
      // if text clipped by right side, need to right-align
      x: xMin + backgroundWidth > imageWidth ? boxWidth - backgroundWidth : 0,
      y: -backgroundHeight - tooltipGap - offset,
    };
  const topRight = () =>
    // there are enough space on the top
    yMin > backgroundHeight && {
      // if text clipped by left side, need to left-align
      x: xMin - backgroundWidth < 0 ? 0 : boxWidth - backgroundWidth,
      y: -backgroundHeight - offset,
    };
  const bottom = () =>
    // there are enough space on the bottom
    yMax < imageHeight - backgroundHeight - tooltipGap && {
      // if text clipped by right side, need to right-align
      x: xMin + backgroundWidth > imageWidth ? boxWidth - backgroundWidth : 0,
      y: boxHeight + offset + tooltipGap,
    };
  const bottomRight = () =>
    // there are enough space on the bottom
    yMax < imageHeight - backgroundHeight && {
      // if text clipped by left side, need to left-align
      x: xMax - backgroundWidth < 0 ? 0 : boxWidth - backgroundWidth,
      y: boxHeight + offset,
    };
  const left = () =>
    // there are enough space on the left
    xMin > backgroundWidth &&
    backgroundWidth < padding && {
      x: -backgroundWidth - offset,
      y: 0,
    };
  const right = () =>
    // there are enough space on the right
    imageWidth - xMax > backgroundWidth &&
    backgroundWidth < padding && {
      x: boxWidth + offset,
      y: 0,
    };
  const inner = () => ({
    x: offset,
    y: offset,
  });
  const innerRight = () => ({
    x: boxWidth - backgroundWidth + offset,
    y: offset,
  });
  return { top, topRight, bottom, bottomRight, left, right, inner, innerRight };
};

export type PositionPriority = keyof ReturnType<typeof buildTextPositionCalculators>;
export const DefaultPositionPriorities: PositionPriority[] = [
  'top',
  'left',
  'right',
  'bottom',
  'inner', // must be the last one
];
export const ReversedPositionPriorities: PositionPriority[] = [
  'bottomRight',
  'right',
  'left',
  'topRight',
  'innerRight', // must be the last one
];
export const DefaultTooltipPositionPriorities: PositionPriority[] = ['top', 'bottom'];

export const calcAnnotationTextPositions = (
  props: TextPositionCalculatorProps,
  priorities: PositionPriority[] = DefaultPositionPriorities,
) => {
  const { scale, text } = props;
  const fontSize = 12 / scale;
  const backgroundHeight = fontSize * 1.5;
  const backgroundWidth = getTextWidth(text) / scale + 2 * fontSize;

  const rectProps = {
    x: 0,
    y: 0,
    width: backgroundWidth,
    height: backgroundHeight,
  };
  const calculators = buildTextPositionCalculators(props);
  for (const priority of priorities) {
    const position = calculators[priority]();
    if (position) {
      rectProps.x = position.x;
      rectProps.y = position.y;
      break;
    }
  }

  const textProps = {
    textAnchor: 'middle' as const,
    alignmentBaseline: 'middle' as const,
    x: (rectProps.x as number) + backgroundWidth * 0.5,
    y: (rectProps.y as number) + backgroundHeight * 0.5,
  };

  return [textProps, rectProps] as [typeof textProps, typeof rectProps];
};

export const calcAnnotationTooltipPositions = (
  props: TextPositionCalculatorProps & { arrowSize: { width: number; height: number } },
  priorities: PositionPriority[] = DefaultPositionPriorities,
) => {
  const { xMin, xMax, yMin, scale, text, arrowSize } = props;
  const fontSize = 12 / scale;
  const backgroundHeight = fontSize * 2;
  const backgroundWidth = getTextWidth(text) / scale + 2 * fontSize;
  const boxWidth = xMax - xMin;

  const rectProps = {
    x: 0,
    y: 0,
    width: backgroundWidth,
    height: backgroundHeight,
    arrowX: 0,
    arrowY: 0,
    arrowRotate: 0,
  };
  const calculators = buildTextPositionCalculators(props, fontSize); // fontSize is suitable for the gap between tooltip and annotation
  for (const priority of priorities) {
    const position = calculators[priority]();
    if (position) {
      rectProps.x = xMin + position.x;
      rectProps.y = yMin + position.y;
      if (priority === 'top') {
        rectProps.arrowX = Math.min(xMin + boxWidth / 2, xMin + backgroundWidth - arrowSize.width);
        rectProps.arrowY = yMin + position.y + backgroundHeight - 1;
      } else if (priority === 'bottom') {
        rectProps.arrowX = Math.min(xMin + boxWidth / 2, xMin + backgroundWidth - arrowSize.width);
        rectProps.arrowY = yMin + position.y;
        rectProps.arrowRotate = 180;
      }
      break;
    }
  }

  return rectProps as typeof rectProps;
};

const bitmapDataUrlCache = new LRUCache<string, string>({
  max: 200,
  maxAge: 1000 * 60 * 15, // 15 min
});

const PreviewBaseEdge = 512;

/**
 * A prediction mask canvas is borrowed, not owned: the hook that produced it frees older
 * generations to bound renderer memory, and freeing one zeroes that canvas in place. Only the
 * commit a mask arrives on is guaranteed to still hold its pixels, so copy it there and encode
 * from the copy. Reading the borrowed canvas from anything deferred - a debounce, an idle
 * callback - can land after the release, and `convertToBlob` on a zeroed canvas rejects with
 * `IndexSizeError`.
 */
const copyForPreview = (source: OffscreenCanvas, compress?: boolean) => {
  if (!source.width || !source.height) {
    return undefined;
  }
  const scale = compress ? Math.min(1, PreviewBaseEdge / Math.max(source.width, source.height)) : 1;
  const width = Math.max(1, Math.floor(source.width * scale));
  const height = Math.max(1, Math.floor(source.height * scale));
  const copy = new OffscreenCanvas(width, height);
  const context = copy.getContext('2d')!;
  context.imageSmoothingEnabled = false;
  context.drawImage(source, 0, 0, width, height);
  return copy;
};

/**
 * When used for preview we resize the canvas to 512 x 512;
 * For the others we keep the original size.
 */
export const useCompressedBitMapDataUrl = (
  compressedBitMap: string | OffscreenCanvas,
  color: string,
  width: number,
  height: number,
  compress?: boolean,
  opacity: number = 0.5,
) => {
  const [dataUrl, setDataUrl] = useState<string | undefined>();

  // Only the canvas branch mints urls this hook owns. The string branch publishes its urls through
  // `bitmapDataUrlCache`, where other components hold the same string, so revoking one there would
  // blank their previews.
  const ownedUrl = useRef<string>();
  useEffect(
    () => () => {
      if (ownedUrl.current) {
        URL.revokeObjectURL(ownedUrl.current);
      }
    },
    [],
  );

  // `useDebouncedEffect` discards whatever its factory returns, so a run has no cleanup to cancel a
  // settled encode with. This is what tells a late one it has been superseded, and it has to move
  // on the input change rather than inside the debounced body: that body does not run for another
  // 500ms, and does not run at all when the new inputs hit the cache, so an encode still in flight
  // would otherwise go on looking current and publish its mask against the new geometry.
  //
  // A layout effect rather than a passive one, so it runs inside the commit: a passive effect is
  // scheduled, and an encode settling in the microtasks before it runs would still see the old
  // generation and publish over what the commit just put on screen.
  const decodeGeneration = useRef(0);
  useLayoutEffect(() => {
    decodeGeneration.current++;
  }, [compressedBitMap, color, width, height, compress, opacity]);

  // The string form decodes into a canvas this hook owns, so deferring it is safe - and worth it,
  // since the run-length decode is the expensive part.
  useDebouncedEffect(() => {
    if (typeof compressedBitMap !== 'string') {
      return;
    }
    // Directly return cached data url if hit cache
    const cacheKey = JSON.stringify({
      compressedBitMap,
      color,
      width,
      height,
      compress,
      opacity,
    });
    if (bitmapDataUrlCache.has(cacheKey)) {
      setDataUrl(bitmapDataUrlCache.get(cacheKey));
      return;
    }
    const generation = decodeGeneration.current;
    const bitMap = runLengthDecode(compressedBitMap);
    const offscreen = createPixelatedCanvas(
      { width, height },
      ({ i }) => bitMap[i] === '1',
      color,
      opacity,
      compress
        ? { baseWidth: PreviewBaseEdge, baseHeight: PreviewBaseEdge }
        : { baseWidth: width, baseHeight: height },
    );
    offscreen
      .convertToBlob()
      .then(value => {
        const imgSrc = URL.createObjectURL(value);
        // keyed and shared, so it is worth caching even for a run nothing is waiting on any more
        bitmapDataUrlCache.set(cacheKey, imgSrc);
        if (generation === decodeGeneration.current) {
          setDataUrl(imgSrc);
        }
      })
      .catch(() => {
        if (generation === decodeGeneration.current) {
          setDataUrl(undefined);
        }
      })
      // This canvas is owned rather than borrowed, but it is the same backing store: the main
      // viewer renders these uncompressed, so it is the size of the whole cropped mask, and the
      // blob is its only purpose. Leaving it to GC strands one per media or threshold change.
      .finally(() => {
        offscreen.width = 0;
        offscreen.height = 0;
      });
  }, [compressedBitMap, color, width, height, compress, opacity]);

  // The canvas form is borrowed, so it has to be read on the commit it arrives on, never from the
  // debounce above. See `copyForPreview`.
  useLayoutEffect(() => {
    if (typeof compressedBitMap === 'string') {
      return;
    }
    // Anything this effect cannot encode has to leave the preview empty rather than untouched. The
    // url from the previous mask outlives this effect, but the geometry around it does not: the
    // scaler and the absolute position have already moved to the new annotation, so keeping the old
    // url draws the previous class's mask stretched into this one's box.
    const publish = (imgSrc?: string) => {
      if (ownedUrl.current) {
        URL.revokeObjectURL(ownedUrl.current);
      }
      ownedUrl.current = imgSrc;
      setDataUrl(imgSrc);
    };

    const copy = copyForPreview(compressedBitMap, compress);
    if (!copy) {
      publish(undefined);
      return;
    }
    let cancelled = false;
    const release = () => {
      copy.width = 0;
      copy.height = 0;
    };
    copy
      .convertToBlob()
      .then(value => {
        if (!cancelled) {
          publish(URL.createObjectURL(value));
        }
      })
      // Encoding can fail on its own - an allocation failure on a large uncompressed mask - and
      // there is nothing to show when it does. Catching is not optional: `finally` re-throws, so
      // without this a rejection escapes the effect as an unhandled promise rejection.
      .catch(() => {
        if (!cancelled) {
          publish(undefined);
        }
      })
      // The blob is the copy's whole purpose, so the pixels are dead the moment it exists. Freeing
      // them here keeps at most one copy alive per preview: leaving it to GC piles up a set per
      // mask generation, which on a large media is the leak this whole change exists to avoid.
      // `convertToBlob` has already snapshotted by the time it settles, so this cannot race it.
      .finally(release);
    return () => {
      cancelled = true;
      // Cancelling stops the result being published but not the encode, which is not cancellable -
      // so waiting for it to settle holds a copy of every superseded mask until it does. When
      // generations land faster than they encode that is several media-sized backing stores at
      // once. Nothing is going to read these pixels now, and a `convertToBlob` already reading
      // them has taken its snapshot; if it has not, it rejects and the catch above discards a
      // result this run would have thrown away anyway.
      release();
    };
  }, [compressedBitMap, compress]);

  return dataUrl;
};
